import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ReadReceipt } from 'api/conversations';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { useUpdateLastSeen } from 'hooks/use-conversations';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useUser } from 'hooks/use-user';
import { getUsersByIds } from 'utils/idb-utils';
import {
  globalConversationQueryKey,
  GlobalConversationQueryResult,
} from './utils';

export const useConversationReadReceiptSocketUpdate = (projectId: number) => {
  const queryClient = useQueryClient();
  const queryKey = globalConversationQueryKey.byProjectId(projectId);
  const {
    user: { userId: myId },
  } = useUser();
  const { mutate: updateLastSeen } = useUpdateLastSeen();

  const handleSocketUpdates = useCallback(
    async (socketData: SocketData<ReadReceipt>) => {
      const { data } = socketData;

      const userIds = data.members.map(({ user }) => user.userId);

      getUsersByIds(userIds).then((users) => {
        const members = data.members.map((member) => ({
          ...member,
          user: users[member.user.userId],
        }));

        return updateLastSeen({
          context: { id: data.entityId, type: data.entityType },
          members,
        });
      });

      if (socketData.actionBy !== myId) {
        return;
      }
      const queryData =
        queryClient.getQueryData<GlobalConversationQueryResult>(queryKey);
      const queryConversationData = queryData?.conversations.find(
        (convo) =>
          convo.contextType === data.entityType &&
          convo.entityId === data.entityId,
      );

      if (!queryData || !queryConversationData) {
        return;
      }
      queryData.unread -= 1;
      queryConversationData.unreadMessages = 0;
      queryClient.setQueryData<GlobalConversationQueryResult>(
        queryKey,
        queryData,
      );
    },
    [myId, queryClient, queryKey, updateLastSeen],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Update, ObjectType.ConversationReadReceipt),
  );
};
